import React, { useCallback, useState } from "react"
import { Title, Button, ModalPanel, Checkbox, Radio } from "dash-styled"
import { DownloadType } from "../../../../../../Hooks/useJobListDownload"
import { AppDownloadSettings } from "../../../../../../Models/API/AppSettings"
import { AnnotationTitleWithKeys } from "../../../../../../Helpers/Schema/Schema"
import AdvancedSettings from "./AdvancedSettings"

export interface DownloadSettingsProps {
    appDownloadSettings: AppDownloadSettings
    onDownloadSettingsChanged: (downlodSetings: AppDownloadSettings) => void
    annotationTitles: AnnotationTitleWithKeys[]
    isFlattenTablesEnabled: boolean
    isIncludeAnnotationsEnabled: boolean
    isSendEmailEnabled: boolean
    isCustomFileNamesEnabled: boolean
}

interface DownloadTypeModalProps {
    itemCount: number
    name: {
        singular: string
        plural: string
    }
    type: DownloadType
    willDownload: boolean
    hasFlatten?: boolean
    onSetType: (type: DownloadType) => void
    onConfirm?: () => void
    onClose: () => void
    resetWithFeedback?: () => void
    isMessages?: boolean
    defaultType?: DownloadType
    allowCsvDownload?: boolean
    allowResponseDataDownload?: boolean
    downloadSettings?: DownloadSettingsProps
}

export function DownloadTypeModal({
    itemCount,
    name,
    onClose,
    onSetType,
    type,
    onConfirm,
    hasFlatten = true,
    willDownload,
    isMessages = false,
    defaultType = "flat_csv",
    allowCsvDownload = true,
    allowResponseDataDownload = false,
    downloadSettings
}: DownloadTypeModalProps) {
    const [isExpanded, setIsExanded] = useState(isMessages)

    const toggleIsExpanded = useCallback(() => {
        if (isExpanded) {
            onSetType(defaultType)
            setIsExanded(false)
        } else {
            setIsExanded(true)
        }
    }, [defaultType, isExpanded, onSetType])

    const handleSetTypeCSV = useCallback(() => {
        onSetType(defaultType)
    }, [defaultType, onSetType])

    const handleSetTypeJSON = useCallback(() => {
        onSetType("json")
    }, [onSetType])

    const handleSetTypeResponseData = useCallback(() => {
        onSetType("response_data")
    }, [onSetType])

    const handleSetTypeFlatCSV = useCallback(() => {
        const newValue = type === "flat_csv" ? "csv" : "flat_csv"
        downloadSettings?.onDownloadSettingsChanged({
            ...downloadSettings.appDownloadSettings,
            expandOutputColumns: newValue === "flat_csv"
        })

        onSetType(newValue)
    }, [downloadSettings, onSetType, type])

    const handleSendEmailToggle = useCallback(() => {
        downloadSettings?.onDownloadSettingsChanged({
            ...downloadSettings.appDownloadSettings,
            sendEmail: !downloadSettings.appDownloadSettings.sendEmail
        })
    }, [downloadSettings])

    const text = willDownload ? "Download" : "Email me"

    const downloadText = onConfirm
        ? text +
          " " +
          itemCount +
          " " +
          (itemCount > 1 ? name.plural : name.singular)
        : "Preparing download"
    return (
        <ModalPanel
            testId="confirm-download-dialog"
            onClose={onClose}
            className="max-w-2xl w-full"
        >
            <Title component="h2" variant="heading2" weight="semibold">
                Download {itemCount > 1 ? name.plural : name.singular}
            </Title>
            <div className="flex flex-col">
                {allowCsvDownload && (
                    <Radio
                        isChecked={type !== "json" && type !== "response_data"}
                        interactive={true}
                        label="As CSV"
                        onChange={handleSetTypeCSV}
                        value="As CSV"
                        color="violet"
                        className="mb-2 text-rhino-900"
                    />
                )}

                <Radio
                    isChecked={type === "json"}
                    interactive={true}
                    label="As JSON"
                    onChange={handleSetTypeJSON}
                    value="As JSON"
                    color="violet"
                    className="text-rhino-900"
                />

                {allowResponseDataDownload && (
                    <Radio
                        isChecked={type === "response_data"}
                        interactive={true}
                        label="Output data"
                        onChange={handleSetTypeResponseData}
                        value="Output data"
                        color="violet"
                        className="mt-2 text-rhino-900"
                    />
                )}
            </div>
            {hasFlatten && type !== "response_data" && (
                <AdvancedSettings
                    isExpanded={isExpanded}
                    toggleExpanded={toggleIsExpanded}
                    handleSetTypeFlatCSV={handleSetTypeFlatCSV}
                    type={type}
                    downloadSettings={downloadSettings}
                />
            )}
            <div className="flex flex-row justify-end pt-4">
                <div className="flex">
                    <Button
                        onClick={onConfirm}
                        testId="confirm-download"
                        className="w-auto"
                        disabled={!onConfirm}
                    >
                        {downloadText}
                    </Button>
                </div>
                <div className="flex justify-end w-1/3">
                    {downloadSettings?.isSendEmailEnabled && (
                        <Checkbox
                            variant="modern"
                            onChange={handleSendEmailToggle}
                            isChecked={
                                downloadSettings.appDownloadSettings.sendEmail
                            }
                            className="ml-5 text-rhino-900"
                            testId="download-csv-checkbox"
                        >
                            Send Email
                        </Checkbox>
                    )}
                </div>
            </div>
        </ModalPanel>
    )
}
